<script setup>
import {onBeforeMount, reactive, ref} from 'vue'
import {State, clone, getModels} from '@/paks/vu-app'
import PageAppearanceEdit from './PageAppearanceEdit.vue'
import {cleanBoard, removeDashboard, updateDashboard} from './BoardUtils.js'

const props = defineProps({
    modelValue: Object,
})
const emit = defineEmits(['close', 'update:modelValue'])

const page = reactive({
    applying: false,
    askRange: false,
    board: {widgets: []},
    canDesign: true,
    copyName: null,
    deleting: false,
    existing: false,
    multiple: null,
    saving: false,
    showCopy: false,
    type: null,
})

const {Dashboard, Manager} = getModels()

//  Component refs
const primary = ref(null)
const confirm = ref(null)

onBeforeMount(() => {
    let board = clone(props.modelValue)
    page.board = cleanBoard(board)
    page.isPage = board.type == 'page'
    page.existing = !board.name && board.widgets.length == 0 ? false : true
    page.multiple = State.config.features?.dash?.multiple ? true : false
})

async function preSave() {
    let {board, type} = page
    if (type == 'dashboard') {
        if (board.name == '' || board.name == 'New Dashboard') {
            throw new Error('Must set dashboard name')
        }
        if (board.id == null && (await Dashboard.get({name: board.name}))) {
            throw new Error('Dashboard name already in use')
        }
    }
    return true
}

async function apply() {
    let board = page.board
    let refresh
    if (board.refresh) {
        refresh = parseInt(board.refresh)
        if (refresh < 5 || isNaN(refresh)) {
            refresh = 5
        }
    }
    board.refresh = refresh
    board.type = board.type || 'dashboard'

    updateDashboard(board)
    State.app.setNeedSave(board)
    emit('update:modelValue', board)
}

async function save() {
    await apply()
    emit('close')
}

async function copy() {
    let board = (page.board = clone(page.board))
    delete board.id
    board.name = page.copyName
    await save()
}

async function deleteBoard() {
    let board = page.board
    if (!board.name) {
        return
    }
    if (!(await confirm.value.ask(`Do you want to delete the dashboard "${board.name}"?`))) {
        return
    }
    removeDashboard(board)
    State.app.setNeedSave(board)
    emit('close')
}
</script>

<template>
    <vu-form
        class="board-edit"
        help="/doc/ui/dashboard/dashboard-edit.html"
        ref="primary"
        :data="page"
        :pre-save="preSave"
        :save="save"
        :title="`${page.existing ? 'Modify' : 'Add'} ${page.isPage ? 'Page' : 'Dashboard'}`">
        <PageAppearanceEdit v-model="page.board" />

        <div v-if="!page.isPage" class="actions">
            <v-btn size="small" color="primary" type="submit" :loading="page.saving">Save</v-btn>
            <v-btn size="small" color="primary" @click="apply" :loading="page.applying">Apply</v-btn>
            <v-btn v-if="page.board.type == 'dashboard'" size="small" color="primary" @click="page.showCopy = true">
                Copy
            </v-btn>
            <v-btn size="small" color="none" @click="emit('close')">Cancel</v-btn>
            <v-btn
                size="small"
                color="error"
                v-if="page.multiple && page.board.id"
                @click="deleteBoard"
                :loading="page.deleting">
                Delete
            </v-btn>
        </div>
        <vu-confirm ref="confirm" />
    </vu-form>
    <v-dialog v-model="page.showCopy" width="550" content-class="save-as-dialog">
        <v-card>
            <v-card-title class="headline" primary-title>Copy Dashboard</v-card-title>
            <v-card-text class="mt-4 pb-0">
                <label>Enter a unique name for the new dashboard</label>
            </v-card-text>
            <v-card-text>
                <v-text-field v-model="page.copyName" single-line label="Dashboard Name" />
            </v-card-text>
            <v-card-actions class="pb-5 px-5">
                <v-btn ripple color="primary" variant="elevated" :loading="page.saving" type="submit" @click="copy">
                    Save
                </v-btn>
                <v-btn ripple variant="elevated" @click="page.showCopy = false">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss">
.board-edit {
    .pick-board {
        margin: 16px 0 0px 0;
        width: 100%;
        .v-input__control {
            border-color: black;
            border-radius: 4px;
        }
    }
    .v-select-list {
        padding: 0;
        border: solid 1px rgb(var(--v-theme-border-lighten-1));
        border-radius: 0;
    }
    h3 {
        font-weight: normal;
        color: rgb(var(--v-theme-text));
    }
    .layouts {
        margin: 10px 0 20px 0;
    }
    .actions {
        margin: 20px 0 20px 0;
    }
}
</style>
<style lang="scss" scoped>
.copy-dialog {
    .headline {
        background: rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-on-primary));
        font-weight: bold;
        padding: 16px;
    }
}
</style>
